/**
 * Repairs widget (dashboard card).
 *
 * Portfolio-wide repair/SLA progress — same data the Repairs Tracking
 * tab shows, condensed into a card. Self-fetching, same widget-card
 * pattern as every other Dashboard component (RecentComms, LegalUpdates,
 * Legal Exposure Score, Predictive Alerts).
 */
import React, { useEffect, useState } from 'react';
import { awaabService } from '../../../services/api';

export default function RepairsWidget() {
    const [dashboard, setDashboard] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        awaabService.getPortfolioDashboard()
            .then((res: any) => {
                if (cancelled) return;
                const data = res.data?.data;
                setDashboard({
                    open: data?.portfolio?.totalOpenCases,
                    onTrack: data?.slaHealth?.onTrack,
                    atRisk: data?.slaHealth?.atRisk,
                    breached: data?.slaHealth?.breached,
                });
            })
            .catch((e: any) => { if (!cancelled) setError(e?.response?.data?.message || 'Failed to load repairs data'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 overflow-auto h-full">
            <div className="drag-handle cursor-grab active:cursor-grabbing flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-900"><i className="fas fa-toolbox text-blue-500 mr-2"></i>Repairs</h2>
                {dashboard && (
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${dashboard.breached > 0 ? 'bg-red-100 text-red-700' : dashboard.atRisk > 0 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                        {dashboard.breached > 0 ? `${dashboard.breached} breached` : dashboard.atRisk > 0 ? `${dashboard.atRisk} at risk` : 'On track'}
                    </span>
                )}
            </div>

            {loading ? (
                <div className="text-center py-8 text-sm text-gray-400">Loading repairs data…</div>
            ) : error ? (
                <div className="text-center py-8 text-sm text-red-500">{error}</div>
            ) : !dashboard ? (
                <div className="text-center py-8 text-sm text-gray-500">
                    <i className="fas fa-info-circle text-2xl text-gray-300 mb-2 block"></i>
                    No repairs data yet.
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-xl p-3 text-center">
                        <div className="text-2xl font-black text-gray-900">{dashboard.open ?? '—'}</div>
                        <div className="text-xs font-semibold text-gray-600 mt-1">In progress</div>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-3 text-center">
                        <div className="text-2xl font-black text-emerald-700">{dashboard.onTrack ?? '—'}</div>
                        <div className="text-xs font-semibold text-emerald-600 mt-1">On track</div>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-3 text-center">
                        <div className="text-2xl font-black text-amber-700">{dashboard.atRisk ?? '—'}</div>
                        <div className="text-xs font-semibold text-amber-600 mt-1">At risk</div>
                    </div>
                    <div className="bg-red-50 rounded-xl p-3 text-center">
                        <div className="text-2xl font-black text-red-700">{dashboard.breached ?? '—'}</div>
                        <div className="text-xs font-semibold text-red-600 mt-1">Breached</div>
                    </div>
                </div>
            )}
        </div>
    );
}
